import type { VirtualScrollerMethods } from "primevue/virtualscroller";
import {
  computed,
  nextTick,
  onScopeDispose,
  shallowRef,
  toValue,
  watchPostEffect,
  type MaybeRefOrGetter,
  type ShallowRef,
} from "vue";

/** Tables with more rows than this render only the rows in view. */
const VIRTUAL_SCROLL_THRESHOLD = 14;

/** The scroller's mount-time setup, public at runtime but missing from PrimeVue's types. */
type VirtualScrollerInternals = {
  viewInit: () => void;
  unbindResizeListener: () => void;
};

/** The DataTable instance; PrimeVue exposes this method at runtime but omits it from its types. */
export type VirtualScrolledTable = {
  getVirtualScrollerRef: () =>
    | (VirtualScrollerMethods & VirtualScrollerInternals & { $el: HTMLElement })
    | undefined;
};

/**
 * Virtual scrolling for a PrimeVue DataTable that can grow to hundreds of rows: above
 * VIRTUAL_SCROLL_THRESHOLD rows it renders only the rows in view, inside a viewport that fits the
 * rows passing the filters and scrolls past 600px (`.virtual-scrolled-table` in primevue-unstyled.css).
 *
 * The scroller positions rows by `rowHeightPx`, so every row must render exactly that tall: pin it
 * in the table's own styles (`:deep(.pv-datatable-body-row) { height: ... }`) and keep cells on one
 * line, or the offsets drift as the user scrolls.
 *
 * @param table - The DataTable's template ref (`useTemplateRef`).
 * @param rowCount - How many rows the table holds (not how many pass its filters).
 * @param rowHeightPx - The rendered height of one row.
 * @returns `tableProps` to spread onto the DataTable with `v-bind`; `resetScroll` to call from its
 *   `@filter` and `@sort` so a narrowed or re-sorted list starts at its first row; and
 *   `scrollElement`, the viewport while the table is virtual (else null), for a ScrollMoreIndicator.
 */
export function useVirtualScrolledTable(
  table: Readonly<ShallowRef<VirtualScrolledTable | null>>,
  rowCount: MaybeRefOrGetter<number>,
  rowHeightPx: MaybeRefOrGetter<number>
) {
  const scrollElement = shallowRef<HTMLElement | null>(null);

  const isVirtual = computed(
    () => toValue(rowCount) > VIRTUAL_SCROLL_THRESHOLD
  );

  const tableProps = computed(() =>
    isVirtual.value
      ? {
          scrollable: true,
          class: "virtual-scrolled-table",
          virtualScrollerOptions: {
            itemSize: toValue(rowHeightPx),
            numToleratedItems: 20,
          },
        }
      : {
          scrollable: false,
          virtualScrollerOptions: undefined,
        }
  );

  // Post-render, so the scroller has swapped its placeholder for the viewport element. (Also defers
  // the first run past setup, where the row count may read state the component declares later.)
  let scrollerReady = false;
  watchPostEffect(() => {
    const scroller = isVirtual.value
      ? table.value?.getVirtualScrollerRef()
      : undefined;
    // WORKAROUND: PrimeVue 4.5.5's VirtualScroller sizes its viewport only the first time it is
    // enabled. Disabled and enabled again (the rows fell to the threshold and grew past it, e.g.
    // switching from a small device group to a large one) it keeps the empty range of its disabled
    // render and shows no rows. Re-run its mount-time setup on the new viewport element; drop this
    // once VirtualScroller re-initialises when its `disabled` prop turns false.
    if (scroller && !scrollerReady) {
      scroller.unbindResizeListener();
      scroller.viewInit();
    }
    scrollerReady = scroller !== undefined;
    scrollElement.value = scroller?.$el ?? null;
  });

  // The effect above stops at unmount without clearing the viewport it found. useInfiniteScroll
  // re-checks its element after each load, from a promise that outlives the component, and a
  // detached viewport passes that check — so its timer would re-arm forever.
  onScopeDispose(() => {
    scrollElement.value = null;
  });

  const resetScroll = async () => {
    await nextTick();
    if (isVirtual.value) {
      table.value?.getVirtualScrollerRef()?.scrollToIndex(0);
    }
  };

  return { tableProps, resetScroll, scrollElement };
}
